import React, { useState, useEffect } from 'react';
import { formatDate, formatCurrency, formatCpfCnpj, formatPercentage } from '../../utils/formatters';
import { calculoService, CalculoData, getClienteNome, getClienteEmail, getClienteTelefone, getClienteCpfCnpj, getClienteTipoPessoa, getProdutoNome, getSituacaoNome } from '../../services/calculoService';
import { EditCalculoModal } from './EditCalculoModal';
import { EditCotacaoModal } from '../cotacao/EditCotacaoModal';
import { Cotacao } from '../../services/cotacaoService';
import { useNotifier } from '../ui/Notifier';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface CalculoModalProps {
  calculo?: CalculoData;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (calculo: CalculoData) => void;
  onDelete?: (calculo: CalculoData) => void;
  onRefresh?: () => void;
}

export const CalculoModal: React.FC<CalculoModalProps> = ({ 
  calculo, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete,
  onRefresh
}) => {
  const notifier = useNotifier();
  const [activeTab, setActiveTab] = useState<'dados' | 'cotacoes'>('dados');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditCotacaoModalOpen, setIsEditCotacaoModalOpen] = useState(false);
  const [selectedCotacao, setSelectedCotacao] = useState<Cotacao | undefined>(undefined);
  const [loadedCalculo, setLoadedCalculo] = useState<CalculoData | undefined>(calculo);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Buscar dados atualizados do cálculo quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      // Sempre voltar para a primeira aba ao abrir o modal
      setActiveTab('dados');
    }

    const loadCalculoData = async () => {
      if (isOpen && calculo?.id) {
        setLoading(true);
        try {
          const response = await calculoService.getById(calculo.id);
          if (response.success && response.data) {
            setLoadedCalculo(response.data);
          } else {
            setLoadedCalculo(calculo);
          }
        } catch (error) {
          console.error('Erro ao carregar cálculo:', error);
          setLoadedCalculo(calculo);
        } finally {
          setLoading(false);
        }
      }
    };

    loadCalculoData();
  }, [isOpen, calculo?.id]);

  const c = loadedCalculo || {} as CalculoData;

  if (!isOpen) return null;

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (onDelete && calculo) {
      onDelete(calculo);
      setShowDeleteConfirm(false);
    }
  };

  const handleSaveCalculo = async () => {
    // Recarregar dados do cálculo
    if (calculo?.id) {
      try {
        const response = await calculoService.getById(calculo.id);
        if (response.success && response.data) {
          setLoadedCalculo(response.data);
        }
      } catch (error) {
        console.error('Erro ao recarregar cálculo:', error);
      }
    }
    
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleEditCotacao = (cotacao: Cotacao) => {
    setSelectedCotacao(cotacao);
    setIsEditCotacaoModalOpen(true);
  };

  const handleSaveCotacao = async () => {
    setIsEditCotacaoModalOpen(false);
    setSelectedCotacao(undefined);
    await handleSaveCalculo();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1 overflow-auto">
        <div className="bg-white rounded max-w-4xl w-full p-1 shadow-2xl max-h-[95vh] overflow-auto">
          <div className="flex justify-between items-center border-b pb-0.5 mb-1">
            <div>
              <h3 className="text-xs font-medium text-gray-900">Detalhes do Cálculo</h3>
              <p className="text-xs text-gray-500 mt-0">
                Nº {c.calculo || 'Sem identificação'} - {getClienteNome(c)}
              </p>
            </div>
            <button 
              onClick={onClose} 
              aria-label="fechar"
              className="p-0.5 hover:bg-gray-100 rounded transition-colors"
            >
              <svg className="w-2 h-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex border-b mb-1">
                <button
                  className={`px-2 py-0.5 text-xs font-medium transition-colors ${
                    activeTab === 'dados'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('dados')}
            >
              Dados do Cálculo
            </button>
            <button
              className={`px-2 py-0.5 text-xs font-medium transition-colors ${
                activeTab === 'cotacoes'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('cotacoes')}
            >
              Cotações ({c.cotacoes?.length || 0})
            </button>
          </div>

          {/* Tab Content */}
          <div className="space-y-1">
            {activeTab === 'dados' && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-gray-500 font-medium">Nº Cálculo:</span>
                  <p className="font-semibold text-gray-900">{c.calculo || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Cliente:</span>
                  <p className="font-semibold text-gray-900">{getClienteNome(c)}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">CPF/CNPJ:</span>
                  <p className="font-medium">{formatCpfCnpj(getClienteCpfCnpj(c))}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Produto:</span>
                  <p className="font-medium">{getProdutoNome(c)}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Item:</span>
                  <p className="font-medium">{c.item || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Situação:</span>
                  <p className={`font-medium ${
                    c.perdido || (typeof c.situacao === 'string' && c.situacao.toLowerCase().includes('perdido')) ? 'text-red-600' :
                    (typeof c.situacao === 'string' && c.situacao.toLowerCase().includes('ganho')) ? 'text-green-600' :
                    'text-blue-600'
                  }`}>
                    {typeof c.situacao === 'string' ? c.situacao : (c.situacao as any)?.nome || '-'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Tipo Pessoa:</span>
                  <p className="font-medium">{getClienteTipoPessoa(c)}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">E-mail:</span>
                  <p className="font-medium truncate">{getClienteEmail(c)}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Celular:</span>
                  <p className="font-medium">{getClienteTelefone(c)}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Valor:</span>
                  <p className="font-semibold text-gray-900">{c.valor ? formatCurrency(c.valor) : '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">% Comissão:</span>
                  <p className="font-medium">{c.percentualComis ? `${formatPercentage(c.percentualComis)}%` : '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Valor Comissão:</span>
                  <p className="font-semibold text-green-600">{c.valorComis ? formatCurrency(c.valorComis) : '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Início Vigência:</span>
                  <p className="font-medium">{c.inicioVigencia ? formatDate(c.inicioVigencia) : '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Data Inclusão:</span>
                  <p className="font-medium">{c.dataInclusa ? formatDate(c.dataInclusa) : '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Usuário Inclusão:</span>
                  <p className="font-medium">{c.usuarioInclusao || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Nível:</span>
                  <p className="font-medium">{c.nivel || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Divisão:</span>
                  <p className="font-medium">{c.divisao || '-'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {c.zerokm && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      0 KM
                    </span>
                  )}
                  {c.perdido && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      Perdido
                    </span>
                  )}
                  {c.aprovado && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Aprovado
                    </span>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'cotacoes' && (
              <div className="text-xs">
                <h4 className="font-medium mb-2">Cotações Recebidas</h4>
                {!c.cotacoes || c.cotacoes.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">Nenhuma cotação encontrada.</div>
                ) : (
                  <div className="space-y-2">
                    {c.cotacoes.map((cot, idx) => (
                      <div 
                        key={cot.id || idx} 
                        className={`p-2 rounded border ${cot.selecionada ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{cot.seguradora}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            {cot.selecionada && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                ✓ Selecionada
                              </span>
                            )}
                            <button
                              onClick={() => handleEditCotacao(cot)}
                              className="text-purple-600 hover:text-purple-800 p-1"
                              title="Editar cotação"
                            >
                              ✏️
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div>
                            <span className="text-gray-500">Plano:</span>
                            <p className="font-medium">{cot.plano || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Tipo Plano:</span>
                            <p className="font-medium">{cot.tipoPlano || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Prêmio Total:</span>
                            <p className="font-semibold text-gray-900">{formatCurrency(cot.premioTotal)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Comissão:</span>
                            <p className="font-medium">{formatPercentage(cot.percentualComis)}% = {formatCurrency(cot.valorComissao)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-1 mt-2 pt-1 border-t">
            {onEdit && (
              <button
                onClick={handleEdit}
                className="px-2 py-0.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
              >
                Editar
              </button>
            )}
            {onDelete && (
              <button
                onClick={handleDelete}
                className="px-2 py-0.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            )}
            <button
              onClick={onClose}
              className="px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
            >
              Fechar
            </button>
          </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de edição de cálculo */}
      <EditCalculoModal
        calculo={loadedCalculo}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveCalculo}
      />

      {/* Modal de edição de cotação */}
      {selectedCotacao && (
        <EditCotacaoModal
          cotacao={selectedCotacao}
          isOpen={isEditCotacaoModalOpen}
          onClose={() => {
            setIsEditCotacaoModalOpen(false);
            setSelectedCotacao(undefined);
          }}
          onSave={handleSaveCotacao}
        />
      )}

      {/* Dialog de confirmação de exclusão */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir Cálculo"
        message="Tem certeza que deseja excluir este cálculo?"
        details={c && c.id ? [
          `Nº: ${c.calculo || 'sem número'}`,
          `Cliente: ${getClienteNome(c)}`,
          `Produto: ${getProdutoNome(c)}`
        ] : []}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="#DC2626"
      />
    </>
  );
};
